import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AuthLayout } from './AuthLayout';
import { PhoneNumberInput } from './components/PhoneNumberInput';
import { SocialLoginButtons } from './components/SocialLoginButtons';
import { registerSchema, RegisterFormData } from './utils/validation';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { authApi } from '../../services/auth.api';
import { useAuthStore } from '../../store/authStore';
import { ApiError } from '../../services/api';
import { useBranding } from '../../utils/branding';

export function Register() {
  const navigate = useNavigate();
  const branding = useBranding();
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const setAuth = useAuthStore((s) => s.setAuth);

  const {
    register,
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isValid, isSubmitted },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      countryCode: '+1',
      phoneNumber: '',
      agreeToTerms: false,
    },
  });

  const countryCode = watch('countryCode');
  const phoneNumber = watch('phoneNumber');

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setApiError(null);

    try {
      await authApi.register({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phoneNumber ? `${data.countryCode}${data.phoneNumber}` : undefined,
      });

      navigate('/auth/verify-otp', {
        state: {
          emailOrPhone: data.email,
          inputType: 'email',
          otpType: 'EMAIL_VERIFY',
          isNewUser: true,
          firstName: data.firstName,
          lastName: data.lastName,
        },
      });
    } catch (error) {
      if (error instanceof ApiError) {
        setApiError(error.message);
      } else {
        setApiError('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Redirect to backend OAuth flow
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    window.location.href = `${apiUrl}/v1/auth/google/login?redirectTo=${encodeURIComponent('/dashboard')}`;
  };

  return (
    <AuthLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-normal text-slate-900 tracking-tight">Create account</h2>
          <p className="mt-1.5 text-sm text-slate-500">Get started with your smart home</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {apiError && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50/80 border border-red-200 animate-fade-in">
              <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900">{apiError}</p>
              </div>
              <button
                type="button"
                onClick={() => setApiError(null)}
                className="text-red-400 hover:text-red-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
          {/* Name Row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <input
                {...register('firstName')}
                type="text"
                placeholder="First name"
                disabled={isLoading}
                autoFocus
                className={cn(
                  'flex h-11 w-full rounded-xl border bg-white px-4 py-2 text-sm transition-all',
                  'placeholder:text-slate-400',
                  'focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  errors.firstName ? 'border-red-500' : 'border-slate-200 hover:border-slate-300'
                )}
              />
              {isSubmitted && errors.firstName && (
                <p className="text-xs text-red-500 px-1">{String(errors.firstName.message)}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <input
                {...register('lastName')}
                type="text"
                placeholder="Last name"
                disabled={isLoading}
                className={cn(
                  'flex h-11 w-full rounded-xl border bg-white px-4 py-2 text-sm transition-all',
                  'placeholder:text-slate-400',
                  'focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  isSubmitted && errors.lastName ? 'border-red-500' : 'border-slate-200 hover:border-slate-300'
                )}
              />
              {isSubmitted && errors.lastName && (
                <p className="text-xs text-red-500 px-1">{String(errors.lastName.message)}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <input
              {...register('email')}
              type="email"
              placeholder="Email address"
              disabled={isLoading}
              className={cn(
                'flex h-11 w-full rounded-xl border bg-white px-4 py-2 text-sm transition-all',
                'placeholder:text-slate-400',
                'focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900',
                'disabled:cursor-not-allowed disabled:opacity-50',
                isSubmitted && errors.email ? 'border-red-500' : 'border-slate-200 hover:border-slate-300'
              )}
            />
            {isSubmitted && errors.email && (
              <p className="text-xs text-red-500 px-1">{String(errors.email.message)}</p>
            )}
          </div>

          {/* Phone */}
          <PhoneNumberInput
            countryCode={countryCode}
            phoneNumber={phoneNumber}
            onCountryCodeChange={(code) => setValue('countryCode', code)}
            onPhoneNumberChange={(number) => setValue('phoneNumber', number)}
            error={isSubmitted ? errors.phoneNumber?.message : undefined}
            disabled={isLoading}
          />

          {/* Terms */}
          <div className="flex items-start gap-3">
            <input
              {...register('agreeToTerms')}
              type="checkbox"
              id="agreeToTerms"
              disabled={isLoading}
              className="h-4 w-4 mt-0.5 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
            />
            <label htmlFor="agreeToTerms" className="text-sm text-slate-500 leading-snug">
              I agree to the{' '}
              <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-slate-900 hover:underline font-medium underline-offset-2">
                Terms & Conditions
              </Link>{' '}
              and{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-slate-900 hover:underline font-medium underline-offset-2">
                Privacy Policy
              </Link>
            </label>
          </div>
          {isSubmitted && errors.agreeToTerms && (
            <p className="text-xs text-red-500 px-1">{String(errors.agreeToTerms.message)}</p>
          )}

          {/* Submit */}
          <Button
            type="submit"
            className="w-full h-12 text-sm font-normal bg-slate-900 hover:bg-slate-800 text-white rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
            disabled={isLoading || !isValid}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Creating account...
              </span>
            ) : (
              'Create account'
            )}
          </Button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-100" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-4 text-xs uppercase tracking-widest text-slate-400 font-medium">
                or continue with
              </span>
            </div>
          </div>

          {/* Social Buttons */}
          <SocialLoginButtons
            onGoogleLogin={handleGoogleLogin}
            onAppleLogin={() => {}}
            onMetaLogin={() => {}}
            disabled={isLoading}
          />

          {/* Sign In Link */}
          <p className="text-center text-sm text-slate-500 pt-1">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/auth/login')}
              disabled={isLoading}
              className="font-normal text-slate-900 hover:underline underline-offset-2"
            >
              Sign in
            </button>
          </p>
        </form>
      </div>
    </AuthLayout>
  );
}
